import { get } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import ApiService from '../../../api/restApi';
import { actionTypes } from '../../../constants/actionTypes';
import { removeViewNameFromChartsData, USER_CLASSIFICATION } from '../../common';
import { getSchema, getTestDriveDomain } from '../authActions';
import { dispatchCustomerInfoQuery, dispatchMasterDataQuery } from '../api/common';
import { useSession } from './auth';

export function usePreFetchData() {
  const dispatch = useDispatch();
  const loadersArray = useSelector((state) => state.root.loadersArray || []);
  const session = useSession();

  const fetchTopTrends = async () => {
    if (!loadersArray.includes('TOP-TRENDS') && session?.session?.user?.userType !== USER_CLASSIFICATION.QWA_TOOLS) {
      dispatch({
        type: actionTypes.ADD_LOADER,
        loaderName: 'TOP-TRENDS',
      });

      try {
        const response = await ApiService.get<any>(`${process.env.NEXT_PUBLIC_QUATTR_API_URL}/api/top-trends`);
        if (response.data) {
          dispatch({
            type: actionTypes.SET_TOPTRENDS_LIST,
            topTrendsList: response.data,
          });
        }
      } catch (error) {
        console.log('fetchTopTrends :: Error ::', error);
      } finally {
        dispatch({
          type: actionTypes.REMOVE_LOADER,
          loaderName: 'TOP-TRENDS',
        });
      }
    }
  };

  const fetchCustomerInfo = async (session) => {
    if (!loadersArray.includes('CUSTOMER_INFO') && session?.user?.userType !== USER_CLASSIFICATION.QWA_TOOLS) {
      dispatch({
        type: actionTypes.ADD_LOADER,
        loaderName: 'CUSTOMER_INFO',
      });
      try {
        const testDriveDomain = await getTestDriveDomain();

        const [response, segmentFiltersRes] = await Promise.all([
          dispatchCustomerInfoQuery(dispatch),
          dispatchMasterDataQuery(dispatch, ['domain', 'market_share_segment']),
        ]);
        const schemaName = await getSchema(session);
        let index = response.data.findIndex((data) => {
          return data.cust_code == schemaName;
        });

        index = index == -1 ? 0 : index;
        let defaultDomain = get(response, `data[${index}].domain_url`) || '';

        const lastChar = defaultDomain.substr(-1); // Selects the last character
        if (lastChar != '/') {
          // If the last character is not a slash
          defaultDomain = defaultDomain + '/'; // Append a slash to it.
        }
        if (testDriveDomain && segmentFiltersRes.market_share_segment) {
          const allMarketSegments = segmentFiltersRes.market_share_segment;
          defaultDomain = testDriveDomain;
          const subscribersMkSegment = allMarketSegments.find((i) => i.segment_id && i.segment_id === testDriveDomain);
          if (subscribersMkSegment) {
            dispatch({
              type: actionTypes.SET_MARKET_SEGMENT,
              defaultMarketSegment: [
                {
                  text: subscribersMkSegment.segment_name,
                  value: subscribersMkSegment.segment_id,
                  type: 'market_share_segment',
                  defaultApplied: true,
                },
              ],
            });
          }
        }

        if (defaultDomain || segmentFiltersRes.domain) {
          dispatch({
            type: actionTypes.SET_DOMAIN,
            defaultDomain: defaultDomain || get(segmentFiltersRes, 'domain[0].top_data'),
          });
        }

        if (response.data) {
          dispatch({
            type: actionTypes.SET_CUSTOMER_INFO,
            customerInfo: response.data,
          });
        }
      } catch (error) {
        console.log('prefetch :: fetchCustomerInfo :: Error ::', error);
      } finally {
        dispatch({
          type: actionTypes.REMOVE_LOADER,
          loaderName: 'CUSTOMER_INFO',
        });
      }
    }
  };

  const getUserPermissions = async () => {
    if (
      ![USER_CLASSIFICATION.QWA_TOOLS, USER_CLASSIFICATION.CONTENT_AI].includes(session?.session?.user?.userType || '')
    ) {
      try {
        const userData = await ApiService.get<any>('looker_api/getUserData');

        const permissionsData = await ApiService.post<any>(`looker_api/getPermissions`, {
          roleIds: userData.data.role_ids,
        });
        if (permissionsData.data) {
          dispatch({
            type: actionTypes.SET_LOOKER_PERMISSIONS,
            looker_permissions: permissionsData.data,
          });
          dispatch({
            type: actionTypes.SET_LOOKER_USER_ID,
            lookerUserId: userData.data.id,
          });
        }
      } catch (error) {
        console.log('getUserPermissions :: Error ::', error);
      }
    }
  };

  const fetchUsabilityRecommendation = async () => {
    if (
      !loadersArray.includes('USABILITY-RECOMMENDATIONS') &&
      session?.session?.user?.userType !== USER_CLASSIFICATION.QWA_TOOLS
    ) {
      dispatch({
        type: actionTypes.ADD_LOADER,
        loaderName: 'USABILITY-RECOMMENDATIONS',
      });

      try {
        const response = await ApiService.post<any>(
          `${process.env.NEXT_PUBLIC_QUATTR_API_URL}/api/recommendations/filteredList`,
          { filtersObj: { lh_category: 'Performance' } }
        );
        if (response.data) {
          dispatch({
            type: actionTypes.SET_USABILITY_RECOMMENDATION_LIST,
            usabilityRecommendationList: removeViewNameFromChartsData(response.data),
          });
        }
      } catch (error) {
        console.log('fetchUsabilityRecommendation :: Error ::', error);
      } finally {
        dispatch({
          type: actionTypes.REMOVE_LOADER,
          loaderName: 'USABILITY-RECOMMENDATIONS',
        });
      }
    }
  };

  const getLooksData = async (reset = false) => {
    if (
      !loadersArray.includes('myAnalysisLooks') &&
      ![USER_CLASSIFICATION.QWA_TOOLS, USER_CLASSIFICATION.CONTENT_AI].includes(session?.session?.user?.userType || '')
    ) {
      dispatch({
        type: actionTypes.ADD_LOADER,
        loaderName: 'myAnalysisLooks',
      });
      try {
        const res1 = await ApiService.get<any>('looker_api/getUserData');
        dispatch({
          type: actionTypes.SET_LOOKER_USER_ID,
          lookerUserId: res1.data.id,
        });

        const [personalFolderResponse, embedFolderResponse] = await Promise.all([
          ApiService.get<any>('looker_api/getPersonalFolder'),
          ApiService.get<any>('looker_api/getEmbedFolder'),
        ]);

        let allLooks: object[] = [];
        let allDashboards: object[] = [];

        const embedUserLooks: object[] = [];
        const embedUserDashboards: object[] = [];

        embedFolderResponse.data.looks.forEach((i) => {
          if (!i.deleted) {
            embedUserLooks.push({ ...i, isEmbedUserLook: true });
          }
        });

        embedFolderResponse.data.dashboards.forEach((i) => {
          if (!i.deleted) {
            embedUserDashboards.push({ ...i, isEmbedUserDashboard: true });
          }
        });

        allLooks = [...personalFolderResponse.data.looks.filter((i) => !i.deleted), ...embedUserLooks];
        allDashboards = [...personalFolderResponse.data.dashboards.filter((i) => !i.deleted), ...embedUserDashboards];

        dispatch({
          type: actionTypes.SET_LOOKS_LIST,
          looksList: allLooks,
          reset,
        });

        dispatch({
          type: actionTypes.SET_DASHBOARDS_LIST,
          dashboardsList: allDashboards,
          reset,
        });
      } catch (error) {
        console.log('getLooksData:Error ', error);
      } finally {
        dispatch({
          type: actionTypes.REMOVE_LOADER,
          loaderName: 'myAnalysisLooks',
        });
      }
    }
  };

  return [fetchTopTrends, fetchUsabilityRecommendation, fetchCustomerInfo, getUserPermissions, getLooksData];
}
